import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Settings, Shield, CreditCard, Eye, Cog, Database, FileText, Wrench, Target, ScanLine, ShieldCheck, Briefcase } from "lucide-react";

// Import existing components
import RoleManagement from "./RoleManagement";
import PaymentPoliciesManagement from "./PaymentPoliciesManagement";
import AuditLogs from "./AuditLogs";
import SystemConfiguration from "./SystemConfiguration";
import BackupManagement from "./BackupManagement";
import TemplatesManagement from "./TemplatesManagement";
import BulkDateFix from "../../components/admin/BulkDateFix";
import ObjectivesConfig from "../../components/admin/ObjectivesConfig";
import OCRSettings from "./OCRSettings";
import FneSettings from "./FneSettings";
import JobTitlesManagement from "../../components/admin/JobTitlesManagement";

const SettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "roles";
  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const tabs = [
    {
      id: "roles",
      label: "Rôles & Permissions",
      icon: Shield,
      description: "Gestion des rôles utilisateurs"
    },
    {
      id: "jobtitles",
      label: "Postes",
      icon: Briefcase,
      description: "Fonctions dans l'entreprise"
    },
    {
      id: "templates",
      label: "Templates",
      icon: FileText,
      description: "Modèles d'opérations et contrats"
    },
    {
      id: "payment",
      label: "Politiques de paiement",
      icon: CreditCard,
      description: "Modalités de paiement"
    },
    {
      id: "backup",
      label: "Sauvegarde",
      icon: Database,
      description: "Backup & Restauration"
    },
    {
      id: "audit",
      label: "Logs d'audit",
      icon: Eye,
      description: "Traçabilité des actions"
    },
    {
      id: "system",
      label: "Configuration système",
      icon: Cog,
      description: "Paramètres généraux"
    },
    {
      id: "maintenance",
      label: "Maintenance",
      icon: Wrench,
      description: "Outils de correction données"
    },
    {
      id: "objectives",
      label: "Objectifs",
      icon: Target,
      description: "Configuration des objectifs business"
    },
    {
      id: "ocr",
      label: "Documents & OCR",
      icon: ScanLine,
      description: "Scanner et extraction IA"
    },
    {
      id: "fne",
      label: "FNE DGI",
      icon: ShieldCheck,
      description: "Certification électronique des factures"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gray-100 rounded-xl">
          <Settings className="w-8 h-8 text-gray-700" />
        </div>
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Paramètres</h1>
          <p className="text-gray-500">Configuration et administration de la plateforme</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="flex w-full overflow-x-auto h-auto p-1 bg-gray-100 rounded-xl">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex flex-col items-center gap-1 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg transition-all"
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{tab.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="mt-6">
          <TabsContent value="roles" className="mt-0">
            <RoleManagementContent />
          </TabsContent>

          <TabsContent value="jobtitles" className="mt-0">
            <JobTitlesManagement />
          </TabsContent>

          <TabsContent value="templates" className="mt-0">
            <TemplatesManagement />
          </TabsContent>
          
          <TabsContent value="payment" className="mt-0">
            <PaymentPoliciesManagement />
          </TabsContent>

          <TabsContent value="backup" className="mt-0">
            <BackupManagement />
          </TabsContent>
          
          <TabsContent value="audit" className="mt-0">
            <AuditLogsContent />
          </TabsContent>
          
          <TabsContent value="system" className="mt-0">
            <SystemConfiguration />
          </TabsContent>

          <TabsContent value="maintenance" className="mt-0">
            <BulkDateFix />
          </TabsContent>

          <TabsContent value="objectives" className="mt-0">
            <ObjectivesConfig />
          </TabsContent>

          <TabsContent value="ocr" className="mt-0">
            <OCRSettings />
          </TabsContent>

          <TabsContent value="fne" className="mt-0">
            <FneSettings />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

// Wrapper pour RoleManagement sans le header (car déjà dans la page)
const RoleManagementContent = () => {
  return (
    <div className="role-management-content">
      <RoleManagement />
    </div>
  );
};

// Wrapper pour AuditLogs sans le header/navigation
const AuditLogsContent = () => {
  return (
    <div className="audit-logs-content">
      <AuditLogs />
    </div>
  );
};

export default SettingsPage;
